import { useTranslation } from 'react-i18next';

import {
  Button,
  ButtonsWrapper,
  Form,
  Icon,
  ICONS,
  Modal,
  NumberInput,
  PLATFORM_THEME_COLORS,
  TextAreaInput,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useCreateDatevNumber } from 'src/hooks/datev-numbers/create-datev-number.hook';
import { useCreateConsultantNumber } from 'src/hooks/consultant-numbers/create-consultant-number.hook';

import { NumbersContainer, DeleteContainer } from './create-datev-number.styles';

const CreateDatevNumber = ({
  isOpened,
  onClose,
  consultantNumberId,
}: {
  isOpened: boolean;
  onClose: () => void;
  consultantNumberId?: string;
}) => {
  const { t } = useTranslation(['common', 'settings', 'employee']);
  const currentCompanyId = useGetCurrentCompanyId();

  const createDatevNumber = useCreateDatevNumber({ onSuccess: onClose });
  const { createConsultantNumber } = useCreateConsultantNumber({ onSuccess: onClose });

  const onSubmitHandler = (values: any) => {
    const { datevNumber, ...rest } = values;
    if (consultantNumberId) {
      return createDatevNumber({
        variables: {
          payload: {
            ...rest,
            consultantNumberId,
            datevNumber: datevNumber.toString(),
            companyId: currentCompanyId,
          },
        },
      });
    }

    createConsultantNumber({
      variables: {
        payload: {
          ...rest,
          datevClientNumbers: values.datevClientNumbers[0] ? values.datevClientNumbers : [],
          consultantNumber: datevNumber.toString(),
          companyId: currentCompanyId,
        },
      },
    });
  };

  return (
    <Modal
      isOpened={isOpened}
      onClose={onClose}
      title={
        consultantNumberId
          ? t('settings:datev.labels.createNumber')
          : t('settings:datev.labels.createConsultantNumber')
      }
    >
      <Form layout="vertical" onFinish={onSubmitHandler}>
        <Form.Item
          name="datevNumber"
          label={
            consultantNumberId
              ? t('settings:datev.labels.clientNumber')
              : t('settings:datev.labels.consultantNumber')
          }
          rules={[{ required: true, message: t('settings:datev.validation.clientNumberRequired') }]}
        >
          <NumberInput format="№#######" placeholder="№0000000" isNumericString />
        </Form.Item>
        <Form.Item
          name="details"
          label={t('employee:labels.details')}
          rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
        >
          <TextAreaInput showCount maxLength={30} placeholder={t('employee:labels.details')} />
        </Form.Item>
        {!consultantNumberId && (
          <Form.List name="datevClientNumbers" initialValue={[undefined]}>
            {(fields, { add, remove }) => (
              <NumbersContainer isIcon={fields.length > 1}>
                <div>
                  {fields.map(field => (
                    <DeleteContainer isIcon={fields.length > 1} key={field.name}>
                      <Form.Item name={field.name} label={t('settings:datev.labels.clientNumber')}>
                        <NumberInput format="№#######" placeholder="№0000000" isNumericString />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Icon
                          icon={ICONS.trash}
                          color={PLATFORM_THEME_COLORS.tone.red}
                          size={22}
                          rightMargin={false}
                          onClick={() => remove(field.name)}
                        />
                      )}
                    </DeleteContainer>
                  ))}
                </div>
                <Button
                  type="text"
                  icon={ICONS.plus}
                  iconColor={PLATFORM_THEME_COLORS.primary.blue}
                  onClick={() => add()}
                >
                  {t('settings:datev.buttons.addClientNumber')}
                </Button>
              </NumbersContainer>
            )}
          </Form.List>
        )}
        <ButtonsWrapper marginTop={16}>
          <Button htmlType="submit" type="primary">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={onClose}>{t('common:buttons.cancel')}</Button>
        </ButtonsWrapper>
      </Form>
    </Modal>
  );
};

export default CreateDatevNumber;
